import React, { useEffect, useState } from 'react';
import { SecondaryBase } from '../Commons/SecondaryBase';
import { ResetCurrentDynamic as BLResetCurrentDynamic } from '../../../utilities/BL/Zones';
import { StartTacoGame, ResetTacoGame, ConnectWebSocketForTacoAdmin, DisconnectWebSocketForTacoAdmin, ShowTacoFrontPageBL } from '../../../utilities/BL/Tacos';


const Package = ({ getOutOfTheDynamic }) => {
    // ==========================================================================
    // ESTADOS DE CONFIGURACIÓN
    // ==========================================================================
    const [durationSeconds, setDurationSeconds] = useState(20);
    const [winMode, setWinMode] = useState(0); // 0: Tiempo, 1: Muerte Súbita (Llegar a meta)
    const [targetTacosToWin, setTargetTacosToWin] = useState(5);

    // ==========================================================================
    // ESTADOS DE LA PARTIDA Y RED
    // ==========================================================================
    const [matchPhase, setMatchPhase] = useState('WAITING');
    const [isWorking, setIsWorking] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [wsStatus, setWsStatus] = useState('idle');

    // Estadísticas en vivo
    const [player1Stats, setPlayer1Stats] = useState({ tacosCompleted: 0, currentStep: 1 });
    const [player2Stats, setPlayer2Stats] = useState({ tacosCompleted: 0, currentStep: 1 });
    const [matchWinner, setMatchWinner] = useState('none');
    
    // Reloj local visual
    const [localTimeLeft, setLocalTimeLeft] = useState(0);

    // ==========================================================================
    // WEBSOCKET
    // ==========================================================================
    useEffect(() => {
        ConnectWebSocketForTacoAdmin(handleIncomingMessage, setWsStatus);
        return () => DisconnectWebSocketForTacoAdmin();
    }, []);

    const handleIncomingMessage = (msg) => {
        const type = msg.type.toLowerCase();
        const payload = msg.payload || {};

        if (type === 'taco.progress' || type === 'taco.finished') {
            setMatchPhase(payload.status === 1 ? 'RUNNING' : payload.status === 2 ? 'FINISHED' : 'WAITING');
            setMatchWinner(payload.winner || 'none');
            
            if (payload.player1) setPlayer1Stats(payload.player1);
            if (payload.player2) setPlayer2Stats(payload.player2);

            // Sincronizar reloj si acaba de iniciar
            if (type === 'taco.progress' && payload.status === 1 && payload.durationSeconds) {
                // Solo inicializamos el reloj local si no estaba corriendo (evita saltos)
                setLocalTimeLeft(prev => prev === 0 ? payload.durationSeconds : prev);
            }
        }
    };

    // Temporizador visual local para el Admin
    useEffect(() => {
        let timer;
        if (matchPhase === 'RUNNING' && localTimeLeft > 0 && winMode === 0) {
            timer = setInterval(() => {
                setLocalTimeLeft(prev => Math.max(0, prev - 1));
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [matchPhase, localTimeLeft, winMode]);

    // ==========================================================================
    // ACCIONES
    // ==========================================================================
    const handleStartGame = async () => {
        setIsWorking(true);
        setErrorMessage('');
        setLocalTimeLeft(durationSeconds); // Reseteamos reloj visual

        const config = {
            durationSeconds: durationSeconds,
            tapsPerStep: 1,     // Fijo: 1 tap = 1 paso
            stepsPerTaco: 4,    // Fijo: 4 pasos = 1 taco
            tacosPerPlate: 5,   // Fijo: 5 tacos = 1 plato
            winMode: winMode,
            targetTacosToWin: targetTacosToWin
        };

        const res = await StartTacoGame(config);
        if (!res.success) {
            setErrorMessage(res.message);
        }
        setIsWorking(false);
    };

    const handleResetGame = async () => {
        setIsWorking(true);
        setErrorMessage('');
        await ResetTacoGame();
        setMatchPhase('WAITING');
        setPlayer1Stats({ tacosCompleted: 0, currentStep: 1 });
        setPlayer2Stats({ tacosCompleted: 0, currentStep: 1 });
        setMatchWinner('none');
        setLocalTimeLeft(0);
        setIsWorking(false);
    };

    const handleShowFrontPage = async () => {
        setIsWorking(true);
        setErrorMessage('');
        const res = await ShowTacoFrontPageBL();
        if (!res.success) setErrorMessage(res.message);
        setIsWorking(false);
    };

    const handleReturnToDashboard = async () => {
        await BLResetCurrentDynamic();
        getOutOfTheDynamic();
    };

    // ==========================================================================
    // HELPERS VISUALES
    // ==========================================================================
    const translateWinner = (winnerStr) => {
        if (winnerStr === 'none') return '';
        if (winnerStr === 'tie') return '¡Empate!';
        return winnerStr === 'player1' ? '¡Ganó Eldo Local!' : '¡Ganó Eldo Visitante!';
    };

    return (
        <SecondaryBase actionToExecute={handleReturnToDashboard}>
            <section className='AdmRunToGoal__Settings'>
                <p className='AdmRunToGoal__Title'>📦 Reto Empacador (1 vs 1)</p>

                <div className='AdmRunToGoal__Settings__Controls'>
                    
                    {/* ESTATUS Y RELOJ */}
                    <div className='AdmRunToGoal__Text--Center border-bottom pb-3 mb-4'>
                        <div className="d-flex justify-content-between align-items-center px-3">
                            <div>
                                <p className="mb-1 text-muted small">Estado de Red</p>
                                <p className="fw-bold mb-0">
                                    <span className={`badge ${wsStatus === 'connected' ? 'bg-success' : 'bg-warning'}`}>
                                        {wsStatus === 'connected' ? 'Conectado al Servidor' : wsStatus}
                                    </span>
                                </p>
                            </div>

                            <div className="bg-dark text-white rounded px-4 py-2 shadow-sm text-center">
                                <p className="mb-0 small text-uppercase" style={{ letterSpacing: '1px', color: '#aaa' }}>
                                    {matchPhase === 'WAITING' ? 'En Espera' : matchPhase === 'RUNNING' ? '¡Empacando!' : 'Terminado'}
                                </p>
                                {winMode === 0 && matchPhase === 'RUNNING' && (
                                    <h3 className="mb-0 fw-bold text-warning" style={{ fontFamily: 'monospace' }}>
                                        {localTimeLeft}s
                                    </h3>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* CONFIGURACIÓN (Solo en espera) */}
                    {matchPhase === 'WAITING' && (
                        <div className="mb-4 bg-light p-3 rounded border">
                            <h6 className="fw-bold mb-3 border-bottom pb-2">Reglas de la Partida</h6>
                            
                            <label className='AdmRunToGoal__Settings__Controls__Labels fw-bold'>Modo de Victoria:</label>
                            <select 
                                className='form-select mb-3'
                                value={winMode}
                                onChange={e => setWinMode(Number(e.target.value))}
                                disabled={isWorking}
                            >
                                <option value={0}>Por Tiempo (El que empaque más artículos al llegar a cero)</option>
                                <option value={1}>Muerte Súbita / Desempate (El primero en llegar a X paquetes)</option>
                            </select>

                            {winMode === 0 ? (
                                <>
                                    <label className='AdmRunToGoal__Settings__Controls__Labels fw-bold'>Duración (Segundos):</label>
                                    <input 
                                        className='form-control mb-3' 
                                        type='number' 
                                        min={5} 
                                        value={durationSeconds} 
                                        onChange={e => setDurationSeconds(Number(e.target.value))} 
                                        disabled={isWorking}
                                    />
                                </>
                            ) : (
                                <>
                                    <label className='AdmRunToGoal__Settings__Controls__Labels fw-bold text-danger'>Paquetes necesarios para ganar automáticamente:</label>
                                    <input 
                                        className='form-control mb-3' 
                                        type='number' 
                                        min={1} 
                                        value={targetTacosToWin} 
                                        onChange={e => setTargetTacosToWin(Number(e.target.value))} 
                                        disabled={isWorking}
                                    />
                                </>
                            )}

                            <button 
                                className={`btn btn-info fw-bold w-100 mt-4 shadow-sm ${isWorking ? 'disabled' : ''}`}
                                onClick={handleShowFrontPage}
                                disabled={isWorking}
                            >
                                <i className="bi bi-display"></i> Mostrar portada
                            </button>

                            <button 
                                className={`btn btn-success fw-bold w-100 mt-2 shadow-sm ${isWorking ? 'disabled' : ''}`}
                                onClick={handleStartGame}
                                disabled={isWorking}
                            >
                                <i className="bi bi-play-fill"></i> Iniciar Reto Empacador
                            </button>
                        </div>
                    )}

                    {/* MONITOREO EN VIVO (Cuando está corriendo o terminó) */}
                    {matchPhase !== 'WAITING' && (
                        <div className="mb-4">
                            <h5 className="fw-bold mb-3 border-bottom pb-2">Marcador en Vivo</h5>
                            
                            <div className="row g-3 mb-4">
                                <div className="col-6">
                                    <div className={`p-3 rounded text-center border ${matchWinner === 'player1' ? 'border-success bg-success text-white' : 'bg-light'}`}>
                                        <h6 className="fw-bold text-uppercase">Eldo local</h6>
                                        <h2 className="fw-black mb-0">{player1Stats.tacosCompleted} <span className="fs-6">Paquetes</span></h2>
                                        <small>Armando paquete {player1Stats.currentStep}/4</small>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className={`p-3 rounded text-center border ${matchWinner === 'player2' ? 'border-success bg-success text-white' : 'bg-light'}`}>
                                        <h6 className="fw-bold text-uppercase">Eldo visitante</h6>
                                        <h2 className="fw-black mb-0">{player2Stats.tacosCompleted} <span className="fs-6">Paquetes</span></h2>
                                        <small>Armando paquete {player2Stats.currentStep}/4</small>
                                    </div>
                                </div>
                            </div>

                            {matchPhase === 'FINISHED' && (
                                <div className="alert alert-warning text-center fw-bold fs-4 shadow-sm mb-4">
                                    <i className="bi bi-trophy-fill text-danger me-2"></i> 
                                    {translateWinner(matchWinner)}
                                </div>
                            )}

                            <button 
                                className={`btn btn-danger fw-bold w-100 shadow-sm ${isWorking ? 'disabled' : ''}`}
                                onClick={handleResetGame}
                                disabled={isWorking}
                            >
                                <i className="bi bi-arrow-counterclockwise"></i> Limpiar y Volver a Configuración
                            </button>
                        </div>
                    )}

                    {errorMessage && <p className='text-danger fw-bold mt-3 text-center'>{errorMessage}</p>}
                </div>
            </section>
        </SecondaryBase>
    );
};

export { Package };